import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import TestsetComponent from '../components/Testset/TestsetComponent';
import TestRegister from './TestRegister';
import { useAuth } from '../context/AuthContext';
import { Button, Modal } from '../components/common';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function TestsetPage() {
  const { getAccessToken, handleApiError, userType } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newEduVer, setNewEduVer] = useState('');
  const [newTreeName, setNewTreeName] = useState('');
  const [error, setError] = useState('');
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateData, setUpdateData] = useState({
    tree_name: '',
    edu_ver: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyData, setCopyData] = useState({
    sourceTreeId: '',
    keyword: ''
  });
  const [viewMode, setViewMode] = useState('tree'); // 'tree' | 'register'
  const scrollContainerRef = React.useRef(null);

  const fetchWithTokenRefresh = useCallback(async (url, options = {}) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }
      
      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();
      
      // 401 에러 먼저 체크하고 즉시 리다이렉트
      if (data.status === 'error' && 
          data.status_code === '401' && 
          data.message === 'Invalid or expired token') {
        handleApiError(new Error('Invalid or expired token'));
        navigate('/signin');
        return null;
      }

      return data;
    } catch (error) {
      console.error('API 호출 에러:', error);
      throw error;
    }
  }, [getAccessToken, navigate, handleApiError]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await fetchWithTokenRefresh('/api/testset/lists');
      // 401 에러로 인해 null이 반환된 경우 함수 종료
      if (!data) {
        return null;
      }
      
      if (data.status === 'success' && Array.isArray(data.data)) {
        setCategories(data.data);
        return data.data; // 카테고리 데이터 반환
      } else {
        setCategories([]);
        return [];
      }
    } catch (error) {
      console.error('카테고리 불러오기 실패:', error);
      setError('카테고리를 불러오는 데 실패했습니다.');
      setCategories([]);
      return [];
    }
  }, [fetchWithTokenRefresh]);

  const fetchTreeData = useCallback(async (treeId) => {
    try {
      const response = await fetchWithTokenRefresh(`/api/testset/${treeId}/components/list`);
      // 401 에러나 다른 에러로 인해 null이 반환된 경우
      if (!response) {
        return;
      }

      if (response.data && response.data.structure) {
        setTreeData(response.data);
      } else {
        setTreeData({ structure: [] });
      }
      setError('');
    } catch (error) {
      console.error('트리 데이터 불러오기 실패:', error);
      setError('트리 데이터를 불러오는 데 실패했습니다');
      setTreeData({ structure: [] });
    }
  }, [fetchWithTokenRefresh]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (activeTab !== null) {
      fetchTreeData(activeTab);
    }
  }, [activeTab, fetchTreeData]);

  const handleAddCategory = async () => {
    try {
      const response = await fetchWithTokenRefresh('/api/testset/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ edu_ver: newEduVer, tree_name: newTreeName })
      });

      if (response.status === 'success') {
        setShowModal(false);
        const savedTreeName = newTreeName;
        const savedEduVer = newEduVer;
        setNewEduVer('');
        setNewTreeName('');
        
        // 카테고리 목록 새로고침
        const newTreeResponse = await fetchWithTokenRefresh('/api/testset/lists');
        if (newTreeResponse.status === 'success' && Array.isArray(newTreeResponse.data)) {
          setCategories(newTreeResponse.data);
          
          // 방금 생성한 트리 찾기
          const newTree = newTreeResponse.data.find(cat => 
            cat.tree_name === savedTreeName && 
            cat.edu_ver === savedEduVer
          );
          
          if (newTree) {
            setActiveTab(newTree.tree_id);
            setSelectedCard(newTree.tree_id);
            scrollToCard(newTree.tree_id);
          }
        }
      } else {
        alert(response.message || '카테고리 추가에 실패했습니다.');
        setShowModal(false);
      }
    } catch (error) {
      alert('카테고리 추가에 실패했습니다. 다시 시도해 주세요.');
      setShowModal(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!activeTab) {
      setError('삭제할 카테고리를 선택해주세요.');
      return;
    }

    const isConfirmed = window.confirm('정말로 이 카테고리를 삭제하시겠습까? 이 작업은 되돌릴 수 없습니다.');

    if (isConfirmed) {
      try {
        await fetchWithTokenRefresh(`/api/testset/${activeTab}/delete`, { method: 'DELETE' });
        fetchCategories();
        setActiveTab(null);
      } catch (error) {
        setError('카테고리 삭제에 실패했습니다.');
      }
    }
  };

  const handleUpdateClick = () => {
    const currentCategory = categories.find(cat => cat.tree_id === activeTab);
    if (currentCategory) {
      setUpdateData({
        tree_name: currentCategory.tree_name,
        edu_ver: currentCategory.edu_ver
      });
      setShowUpdateModal(true);
    } else {
      setError('수정할 카테고리를 선택해주세요.');
    }
  };

  const handleUpdateCategory = async () => {
    if (!activeTab) {
      setError('수정할 카테고리를 선택해세요.');
      return;
    }

    try {
      const data = await fetchWithTokenRefresh(`/api/testset/${activeTab}/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (data.status === 'success') {
        setShowUpdateModal(false);
        fetchCategories();
        setError('');
        setSuccessMessage('카테고리가 성공적으로 수정되었습니다.');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(data.message || '카테고리 수정에 실패했습니다.');
      }
    } catch (error) {
      setError(error.message || '카테고리 수정에 실패했습니다.');
    }
  };

  const handleAddComponent = async (newComponent, parentComponent = null) => {
    try {
      const requestBody = {
        depth: newComponent.depth,
        priority: newComponent.priority,
        comp_name: newComponent.comp_name
      };

      if (parentComponent) {
        requestBody.parent_component = {
          depth: parentComponent.depth,
          priority: parentComponent.priority,
          comp_name: parentComponent.comp_name,
          path_id: parentComponent.path_id,
          order: parentComponent.order
        };
      }

      const response = await fetchWithTokenRefresh(`/api/testset/${activeTab}/components/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (response.status === 'success') {
        fetchTreeData(activeTab);
      } else {
        throw new Error(response.message || '컴포넌트 추가에 실패했습니다.');
      }
    } catch (error) {
      setError(error.message || '컴포넌트 추가에 실패습니다.');
    }
  };

  const handlePriorityUpdate = async () => {
    try {
      const response = await fetchWithTokenRefresh(`/api/testset/${activeTab}/priority`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      alert(response.message || '중요도가 갱신되었습니다.');
      // 중요도 갱신 후 트리 데이터 새로고침
      await fetchTreeData(activeTab);
    } catch (error) {
      alert(error.message || '중요도 갱신에 실패했습니다.');
    }
  };

  const handleCloseableCheck = async () => {
    try {
      const response = await fetchWithTokenRefresh(`/api/testset/${activeTab}/closeable`);
      alert(response.message || '확정 가능 여부를 확인했습니다.');
    } catch (error) {
      alert(error.message || '확정 가능 여부 확인에 실패했습니다.');
    }
  };

  const handleClose = async () => {
    try {
      const response = await fetchWithTokenRefresh(`/api/testset/${activeTab}/close`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      alert(response.message || '성공적으로 확정되었습니다.');
      
      // 확정 후 현재 트리를 선택된 상태로 유지하면서 목록 새로고침
      const currentTreeId = activeTab;
      await fetchCategories();
      // 선택 상태 유지
      setActiveTab(currentTreeId);
      setSelectedCard(currentTreeId);
      scrollToCard(currentTreeId);
    } catch (error) {
      alert(error.message || '확정에 실패했습니다.');
    }
  };

  const handleCopyCategory = async () => {
    if (!copyData.sourceTreeId || !copyData.keyword) {
      setError('복사할 카테고리와 키워드를 입력해주세요.');
      return;
    }

    try {
      // 1. 원본 카테고리 정보 가져오기
      const sourceCategory = categories.find(cat => cat.tree_id === parseInt(copyData.sourceTreeId));
      if (!sourceCategory) {
        setError('원본 카테고리를 찾을 수 없습니다.');
        return;
      }

      // 2. 새 카테고리 생성 (키워드 포함)
      const newTreeName = `${sourceCategory.tree_name}(${copyData.keyword})`;
      const createResponse = await fetchWithTokenRefresh('/api/testset/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          edu_ver: sourceCategory.edu_ver, 
          tree_name: newTreeName 
        })
      });

      if (createResponse.status !== 'success') {
        throw new Error(createResponse.message || '새 카테고리 생성에 실패했습니다.');
      }

      // 3. 카테고리 목록 새로고침하여 새로 생성된 tree_id 가져오기
      const listResponse = await fetchWithTokenRefresh('/api/testset/lists');
      if (listResponse.status === 'success' && Array.isArray(listResponse.data)) {
        const newTree = listResponse.data.find(cat => 
          cat.tree_name === newTreeName && 
          cat.edu_ver === sourceCategory.edu_ver
        );

        if (newTree) {
          // 4. 컴포넌트 복사 API 호출
          const copyResponse = await fetchWithTokenRefresh('/api/testset/tree-copy', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              source_tree_id: parseInt(copyData.sourceTreeId),
              target_tree_id: newTree.tree_id
            })
          });

          if (copyResponse.status === 'success') {
            setShowCopyModal(false);
            setCopyData({ sourceTreeId: '', keyword: '' });
            
            // 카테고리 목록 새로고침 후 새 트리 선택
            await fetchCategories();
            setActiveTab(newTree.tree_id);
            setSelectedCard(newTree.tree_id);
            scrollToCard(newTree.tree_id);
            // 새로 선택된 트리의 데이터 불러오기
            await fetchTreeData(newTree.tree_id);
            setSuccessMessage('카테고리가 성공적으로 복사되었습니다.');
            setTimeout(() => setSuccessMessage(''), 3000);
          } else {
            throw new Error(copyResponse.message || '컴포넌트 복사에 실패했습니다.');
          }
        } else {
          throw new Error('새로 생성된 카테고리를 찾을 수 없습니다.');
        }
      }
    } catch (error) {
      setError(error.message || '카테고리 복사에 실패했습니다.');
      setShowCopyModal(false);
    }
  };

  const memoizedTreeData = useMemo(() => treeData, [treeData]);

  // 선택된 카드를 화면 가운데로 스크롤하는 함수
  const scrollToCard = useCallback((treeId) => {
    if (!scrollContainerRef.current) return;
    
    setTimeout(() => {
      const cardElement = document.querySelector(`[data-tree-id="${treeId}"]`);
      if (cardElement && scrollContainerRef.current) {
        const containerWidth = scrollContainerRef.current.offsetWidth;
        const cardLeft = cardElement.offsetLeft;
        const cardWidth = cardElement.offsetWidth;
        const scrollPosition = cardLeft - (containerWidth / 2) + (cardWidth / 2);
        
        scrollContainerRef.current.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }, 100); // DOM 업데이트를 위한 약간의 지연
  }, []);


  return (
    <Layout>
      <div className="min-h-screen w-full">
        <nav className="mb-0">
        </nav>
        
        <div className="w-full md:flex md:gap-4">
          {/* 좌측 카테고리 사이드바 */}
          <div ref={scrollContainerRef} className="overflow-x-auto md:shrink-0 md:w-[320px] md:overflow-x-hidden">
            <div className="flex gap-3 p-2 min-w-[800px] md:min-w-0 md:flex-col md:gap-2">
              {/* 추가/복사 버튼 */}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowModal(true)}
                  className="flex shrink-0 w-[75px] items-center justify-center p-4 rounded-3xl md:flex-1 md:w-auto md:p-2 md:rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400 transition-all"
                >
                  <svg className="w-8 h-8 md:w-5 md:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>

                {userType === 'manager' && (
                  <button
                    onClick={() => setShowCopyModal(true)}
                    className="flex shrink-0 w-[75px] items-center justify-center p-4 rounded-3xl md:flex-1 md:w-auto md:p-2 md:rounded-2xl border-2 border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 transition-all"
                    title="카테고리 복사"
                  >
                    <svg className="w-8 h-8 md:w-5 md:h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                )}
              </div>

              {/* 카테고리 카드 목록 */}
              {categories
                .sort((a, b) => {
                  if (a.is_closed !== b.is_closed) {
                    return b.is_closed ? 1 : -1;
                  }
                  return a.tree_name.localeCompare(b.tree_name, 'ko');
                })
                .map((category) => (
                <div key={category.tree_id} className="relative flex flex-col" data-tree-id={category.tree_id}>
                  <button
                    className={`flex items-center gap-2 shrink-0 min-w-[150px] p-4 md:shrink md:min-w-0 md:w-full md:p-3 md:text-left ${selectedCard === category.tree_id ? 'rounded-t-3xl md:rounded-t-2xl' : 'rounded-3xl md:rounded-2xl'} border-2 transition-all ${
                      selectedCard === category.tree_id
                        ? 'border-primary bg-primary/10 shadow-lg border-b-0'
                        : category.is_closed
                          ? 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:scale-105 md:hover:scale-100'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md hover:scale-105 md:hover:scale-100'
                    }`}
                    onClick={() => {
                      if (selectedCard === category.tree_id) {
                        setActiveTab(null);
                        setSelectedCard(null);
                        setViewMode('tree');
                      } else {
                        setActiveTab(category.tree_id);
                        setSelectedCard(category.tree_id);
                        setViewMode('tree');
                        scrollToCard(category.tree_id);
                      }
                    }}
                  >
                    <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center shrink-0 ${
                      category.is_closed
                        ? 'bg-primary text-white'
                        : 'border border-gray-300 bg-white text-gray-600'
                    }`}>
                      {category.tree_id}
                    </span>

                    <div className="flex flex-col items-center flex-1 md:items-start md:min-w-0">
                      <span className="text-xs text-gray-500 truncate">{category.edu_ver}</span>
                      <span className="text-sm font-medium truncate">{category.tree_name}</span>
                    </div>
                  </button>

                  {selectedCard === category.tree_id && (
                    <div className="flex rounded-b-3xl md:rounded-b-2xl overflow-hidden border-2 border-t-0 border-primary bg-primary/10">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`'${category.tree_name}' 카테고리를 삭제하시겠습니까?`)) {
                            handleDeleteCategory(category.tree_id);
                          }
                        }}
                        className="flex-1 py-2 px-2 text-xs bg-red-500/80 hover:bg-red-600/70 text-white transition-colors"
                        title="삭제"
                      >
                        삭제
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowUpdateModal(true);
                          setUpdateData({
                            tree_name: category.tree_name,
                            edu_ver: category.edu_ver
                          });
                        }}
                        className="flex-1 py-2 px-2 text-xs bg-green-500/80 hover:bg-green-600/70 text-white transition-colors border-l border-r border-white/20"
                        title="수정"
                      >
                        수정
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!category.is_closed) {
                            alert('트리를 먼저 확정해주세요.');
                            return;
                          }
                          if (window.innerWidth < 768) {
                            navigate(`/test-register/${category.tree_id}`);
                          } else {
                            setViewMode('register');
                          }
                        }}
                        className="flex-1 py-2 px-2 text-xs bg-orange-500/80 hover:bg-orange-600/70 text-white transition-colors"
                        title="문제편성"
                      >
                        문제편성
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 우측 메인 콘텐츠 */}
          <div className="mt-4 md:mt-0 flex-1 min-w-0">
            {error && <div className="mb-4 rounded bg-danger/10 p-3 text-danger">{error}</div>}

            {activeTab ? (
              viewMode === 'register' ? (
                <TestRegister
                  treeId={activeTab}
                  embedded={true}
                  onBack={() => setViewMode('tree')}
                />
              ) : (
                <div className="overflow-x-auto">
                  <TestsetComponent
                    data={memoizedTreeData}
                    tree_id={activeTab}
                    fetchTreeData={fetchTreeData}
                    onPriorityUpdate={handlePriorityUpdate}
                    onCloseableCheck={handleCloseableCheck}
                    onClose={handleClose}
                    isClosed={categories.find(cat => cat.tree_id === activeTab)?.is_closed || false}
                  />
                </div>
              )
            ) : (
              <div className="mt-8 flex flex-col items-center justify-center p-8 text-gray-500">
                <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="text-lg font-medium">학습 구성을 선택해주세요</p>
                <p className="text-sm mt-2"><span className="md:hidden">상단의</span><span className="hidden md:inline">좌측의</span> 카드를 클릭하여 학습 구성을 확인하고 관리할 수 있습니다</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="새 카테고리 추가"
        size="sm"
        footer={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowModal(false)}
            >
              취소
            </Button>
            <Button
              variant="primary"
              onClick={handleAddCategory}
            >
              추가
            </Button>
          </div>
        }
      >
        <form onSubmit={(e) => { e.preventDefault(); handleAddCategory(); }}>
          <div className="mb-4">
            <label htmlFor="eduVer" className="mb-2 block text-sm font-medium text-gray-700">교육과정</label>
            <input
              type="text"
              id="eduVer"
              className="w-full rounded-3xl border border-gray-300 p-2 focus:border-primary focus:ring-primary"
              value={newEduVer}
              onChange={(e) => setNewEduVer(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="treeName" className="mb-2 block text-sm font-medium text-gray-700">과목 이름</label>
            <input
              type="text"
              id="treeName"
              className="w-full rounded-3xl border border-gray-300 p-2 focus:border-primary focus:ring-primary"
              value={newTreeName}
              onChange={(e) => setNewTreeName(e.target.value)}
              required
            />
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        title="카테고리 수정"
        size="sm"
        footer={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowUpdateModal(false)}
            >
              취소
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateCategory}
            >
              수정
            </Button>
          </div>
        }
      >
        <form onSubmit={(e) => { e.preventDefault(); handleUpdateCategory(); }}>
          <div className="mb-4">
            <label htmlFor="updateEduVer" className="mb-2 block text-sm font-medium text-gray-700">교육과정</label>
            <input
              type="text"
              id="updateEduVer"
              className="w-full rounded-3xl border border-gray-300 p-2 focus:border-primary focus:ring-primary"
              value={updateData.edu_ver}
              onChange={(e) => setUpdateData({...updateData, edu_ver: e.target.value})}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="updateTreeName" className="mb-2 block text-sm font-medium text-gray-700">과목 이름</label>
            <input
              type="text"
              id="updateTreeName"
              className="w-full rounded-3xl border border-gray-300 p-2 focus:border-primary focus:ring-primary"
              value={updateData.tree_name}
              onChange={(e) => setUpdateData({...updateData, tree_name: e.target.value})}
              required
            />
          </div>
        </form>
      </Modal>

      {/* 카테고리 복사 모달 */}
      <Modal
        isOpen={showCopyModal}
        onClose={() => {
          setShowCopyModal(false);
          setCopyData({ sourceTreeId: '', keyword: '' });
          setError('');
        }}
        title="카테고리 복사"
        size="sm"
        footer={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowCopyModal(false);
                setCopyData({ sourceTreeId: '', keyword: '' });
                setError('');
              }}
            >
              취소
            </Button>
            <Button
              variant="primary"
              onClick={handleCopyCategory}
            >
              복사
            </Button>
          </div>
        }
      >
        <form onSubmit={(e) => { e.preventDefault(); handleCopyCategory(); }}>
          <div className="mb-4">
            <label htmlFor="sourceTree" className="mb-2 block text-sm font-medium text-gray-700">복사할 카테고리</label>
            <select
              id="sourceTree"
              className="w-full rounded-3xl border border-gray-300 p-2 focus:border-primary focus:ring-primary"
              value={copyData.sourceTreeId}
              onChange={(e) => setCopyData({...copyData, sourceTreeId: e.target.value})}
              required
            >
              <option value="">카테고리를 선택하세요</option>
              {categories
                .filter(cat => {
                  // 컴포넌트가 있는 카테고리만 표시
                  return true; // 일단 모든 카테고리 표시
                })
                .map(cat => (
                  <option key={cat.tree_id} value={cat.tree_id}>
                    {cat.edu_ver} - {cat.tree_name} {cat.is_closed ? '(확정)' : ''}
                  </option>
                ))
              }
            </select>
          </div>
          <div className="mb-4">
            <label htmlFor="keyword" className="mb-2 block text-sm font-medium text-gray-700">추가 키워드</label>
            <input
              type="text"
              id="keyword"
              className="w-full rounded-3xl border border-gray-300 p-2 focus:border-primary focus:ring-primary"
              value={copyData.keyword}
              onChange={(e) => setCopyData({...copyData, keyword: e.target.value})}
              placeholder="예: 2학기, 심화"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              원본 카테고리 이름 뒤에 ({copyData.keyword || '키워드'}) 형태로 추가됩니다.
            </p>
          </div>
        </form>
      </Modal>

      {successMessage && (
        <div className="relative rounded border border-success bg-success/10 px-4 py-3 text-success" role="alert">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}
    </Layout>
  );
}

export default TestsetPage;
